/**
 * Prévisualisation dynamique de la gravure sur le packshot produit.
 *
 * Lit les attributs data-engraving-* posés sur .modalGravure :
 *  - data-engraving-collection : clé de collection (tag "collection_xxx")
 *  - data-engraving-type       : type de produit (bracelet, bague, collier...)
 *  - data-engraving-shape      : forme de médaille (metafield custom.medal_shape : rond / rectangle)
 *  - data-engraving-images     : URLs des packshots séparées par "|"
 *  - data-engraving-ratios     : ratios largeur/hauteur correspondants
 *  - data-engraving-zone       : JSON optionnel (metafield sf_personalization.preview_zone)
 *                                qui surcharge la zone par défaut de la collection.
 *  - data-engraving-fields     : JSON construit par le snippet à partir des metafields produit
 *                                custom.engraving_x / _y / _width / _size / _curve / _rotate / _align / _back / _color.
 *                                Prioritaire sur tout le reste, champ par champ.
 *  - data-engraving-custom-image / -custom-ratio : image dédiée (metafield custom.engraving_image, type fichier),
 *                                utilisée à la place du packshot quand elle est renseignée.
 *
 * Format d'une zone (rotate en degrés, tout le reste en % de la LARGEUR de l'image) :
 *  { "image": 0, "x": 0, "y": -10, "width": 30, "rotate": 0, "size": 3, "curve": 0, "back": false }
 *  - image  : index du packshot à utiliser (0 = image principale)
 *  - x, y   : décalage du texte par rapport au CENTRE de l'image (x négatif = gauche, y négatif = haut).
 *             Les deux axes sont en % de la largeur : la position ne dépend pas de la hauteur du packshot.
 *  - width  : largeur maximale du texte
 *  - size   : hauteur de police en % de la largeur de l'image
 *  - curve  : courbure du texte en % de la largeur de la zone (positif = bombé vers le haut, négatif = creux)
 *  - align  : "center" (x = centre du texte, défaut), "left" (x = début du texte) ou "right" (x = fin du texte)
 *  - back   : true si la gravure est au dos (pas de vue disponible) => cartouche sous le bijou
 *  - backLabel : "clasp" pour la mention "au dos du fermoir" (câble, beads), sinon "au dos du bijou"
 *  - color  : couleur CSS du texte sur l'image (défaut : gris foncé translucide défini en SCSS)
 */
(function () {
  'use strict';

  // Zones par défaut, issues du guide "le_gramme_engraving_placements.pdf".
  // Clé, de la plus précise à la plus générale :
  // "collection/type/forme", "collection/forme", "type/forme", "collection/type", "collection".
  var DEFAULT_ZONES = {
    'ruban/bracelet': { image: 0, x: 0, y: -8.8, width: 30, rotate: 0, size: 2.4, curve: 3 },
    'ruban/bague': { image: 0, x: 0, y: -5.6, width: 22, rotate: 0, size: 3, curve: 3 },
    'jonc/bracelet': { image: 0, x: 0, y: -13.6, width: 30, rotate: 0, size: 3, curve: 1.5 },
    'jonc/bague': { image: 0, x: 0, y: -4, width: 16, rotate: 0, size: 2.6, curve: 3 },
    'segment/bague': { image: 0, x: 0, y: -8, width: 20, rotate: 0, size: 2.6 },
    'cordon': { image: 1, x: -15, y: -14.8, width: 12, rotate: 0, size: 2, align: 'left' },
    // Colliers : la forme vient du metafield custom.medal_shape (rond / rectangle).
    'collier/rectangle': { image: 1, x: 4.5, y: 32.4, width: 22, rotate: -90, size: 1.7, align: 'left' },
    'collier/rond': { image: 1, x: -4.5, y: 30, width: 12, rotate: 32, size: 1.8, curve: -22 },
    'collier': { image: 1, x: 4.5, y: 32.4, width: 22, rotate: -90, size: 1.7, align: 'left' },
    'medailles/rectangle': { image: 1, x: 4.5, y: 32.4, width: 22, rotate: -90, size: 1.7, align: 'left' },
    'medailles/rond': { image: 1, x: -4.5, y: 30, width: 12, rotate: 32, size: 1.8, curve: -22 },
    'medailles': { image: 1, x: 4.5, y: 32.4, width: 22, rotate: -90, size: 1.7, align: 'left' },
    // Gravure au dos du fermoir, aucune vue de dos disponible.
    'cable': { image: 0, back: true, backLabel: 'clasp' },
    'cable-sur-chaine': { image: 0, back: true, backLabel: 'clasp' },
    'beads': { image: 0, back: true, backLabel: 'clasp' },
    'ceramique-noire': { image: 0, back: true, backLabel: 'clasp' },
    'nato': { image: 0, back: true, backLabel: 'clasp' },
    'orlebar-brown': { image: 0, back: true, backLabel: 'clasp' }
  };

  var FALLBACK_ZONE = { image: 0, x: 0, y: 0, width: 40, rotate: 0, size: 3, curve: 0, align: 'center' };

  var state = {
    zone: null,
    previews: [],
    text: ''
  };

  function normalizeKey(value) {
    return (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function parseZone(raw) {
    if (!raw) {
      return null;
    }
    try {
      var parsed = JSON.parse(raw);
      // Metafield de type texte : le JSON est encodé une seconde fois.
      if (typeof parsed === 'string') {
        parsed = JSON.parse(parsed);
      }
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      console.warn('[engraving-preview] zone JSON invalide', error);
      return null;
    }
  }

  // Valeurs acceptées pour la forme, ramenées à "rond" ou "rectangle".
  var SHAPE_ALIASES = {
    rond: 'rond', ronde: 'rond', round: 'rond', circle: 'rond', cercle: 'rond',
    rectangle: 'rectangle', rectangulaire: 'rectangle', rect: 'rectangle'
  };

  function resolveZone(modal) {
    var override = parseZone(modal.getAttribute('data-engraving-zone'));
    var fields = parseZone(modal.getAttribute('data-engraving-fields'));
    var collection = normalizeKey(modal.getAttribute('data-engraving-collection'));
    var type = normalizeKey(modal.getAttribute('data-engraving-type'));
    var shape = normalizeKey(modal.getAttribute('data-engraving-shape'));
    shape = SHAPE_ALIASES[shape] || shape;

    // La forme de médaille, quand elle est renseignée, prime sur le tag collection :
    // un collier rectangle tagué "céramique noire" garde le rendu des colliers rectangle.
    var candidates = [];
    if (shape) {
      candidates.push(collection + '/' + type + '/' + shape, collection + '/' + shape, type + '/' + shape);
    }
    candidates.push(collection + '/' + type, collection);

    var base = FALLBACK_ZONE;
    for (var i = 0; i < candidates.length; i++) {
      if (DEFAULT_ZONES[candidates[i]]) {
        base = DEFAULT_ZONES[candidates[i]];
        break;
      }
    }

    var zone = Object.assign({}, FALLBACK_ZONE, base, override || {}, fields || {});
    ['x', 'y', 'width', 'size', 'curve', 'rotate', 'image'].forEach(function (key) {
      if (typeof zone[key] === 'string') {
        zone[key] = parseFloat(zone[key].replace(',', '.'));
      }
    });
    if (typeof zone.back === 'string') {
      zone.back = zone.back === 'true';
    }
    return zone;
  }

  function applyZone(preview, zone, images, ratios) {
    var image = preview.querySelector('[data-engraving-preview-image]');
    var index = Math.min(Math.max(parseInt(zone.image, 10) || 0, 0), Math.max(images.length - 1, 0));

    if (image && images[index]) {
      image.src = images[index];
      image.removeAttribute('srcset');
    }
    if (ratios[index]) {
      preview.style.setProperty('--gravure-preview-ratio', ratios[index]);
    }

    preview.style.setProperty('--gravure-preview-rotate', (zone.rotate || 0) + 'deg');
    // Couleur forcée : on coupe aussi le mode de fusion "multiply", qui rendrait
    // invisible un texte clair sur une matière sombre (céramique noire).
    if (zone.color) {
      preview.style.setProperty('--gravure-preview-color', zone.color);
    } else {
      preview.style.removeProperty('--gravure-preview-color');
    }
    preview.classList.toggle('gravure-preview--custom-color', !!zone.color);
    preview.classList.toggle('gravure-preview--back', !!zone.back);

    var backLabel = preview.querySelector('.gravure-preview__back-label');
    if (backLabel) {
      var labelKey = zone.backLabel === 'clasp' ? 'labelClasp' : 'labelDefault';
      if (backLabel.dataset[labelKey]) {
        backLabel.textContent = backLabel.dataset[labelKey];
      }
    }
    layout(preview);
  }

  /**
   * Positionne le texte par rapport à l'image réellement affichée (object-fit: contain),
   * quelle que soit la forme du conteneur. Les % de la zone sont convertis en px.
   */
  function layout(preview) {
    var svg = preview.querySelector('.gravure-preview__text');
    var image = preview.querySelector('[data-engraving-preview-image]');
    if (!svg || !image || !state.zone) {
      return;
    }

    var box = preview.getBoundingClientRect();
    if (!box.width || !box.height) {
      return;
    }

    var ratio = parseFloat(preview.style.getPropertyValue('--gravure-preview-ratio'));
    if (!ratio && image.naturalWidth && image.naturalHeight) {
      ratio = image.naturalWidth / image.naturalHeight;
    }
    ratio = ratio || 1;

    // Dimensions de l'image contenue dans la boîte.
    var width = box.width;
    var height = box.height;
    if (width / height > ratio) {
      width = height * ratio;
    } else {
      height = width / ratio;
    }
    var offsetX = (box.width - width) / 2;
    var offsetY = (box.height - height) / 2;

    // Selon l'alignement, (x, y) désigne le début, le centre ou la fin du texte.
    // Le décalage suit la direction d'écriture, donc la rotation de la zone.
    var zoneWidth = (state.zone.width / 100) * width;
    var centerX = offsetX + width / 2 + (state.zone.x / 100) * width;
    var centerY = offsetY + height / 2 + (state.zone.y / 100) * width;
    var angle = ((state.zone.rotate || 0) * Math.PI) / 180;
    var shift = state.zone.align === 'left' ? 1 : state.zone.align === 'right' ? -1 : 0;
    centerX += shift * (zoneWidth / 2) * Math.cos(angle);
    centerY += shift * (zoneWidth / 2) * Math.sin(angle);

    preview.style.setProperty('--gravure-preview-x', centerX + 'px');
    preview.style.setProperty('--gravure-preview-y', centerY + 'px');

    drawText(svg, zoneWidth, (state.zone.size / 100) * width, (state.zone.curve || 0) / 100, state.zone.align);
  }

  /**
   * Trace l'arc sur lequel le texte est posé et ajuste la police pour tenir dans la zone.
   * zoneWidth et fontSize en px, curve en fraction de la largeur de la zone, align = left | center | right.
   */
  function drawText(svg, zoneWidth, fontSize, curve, align) {
    var path = svg.querySelector('[data-engraving-preview-path]');
    var textNode = svg.querySelector('text');
    var textPath = svg.querySelector('textPath');
    if (!path || !textNode || !textPath) {
      return;
    }

    if (align === 'left') {
      textNode.setAttribute('text-anchor', 'start');
      textPath.setAttribute('startOffset', '0%');
    } else if (align === 'right') {
      textNode.setAttribute('text-anchor', 'end');
      textPath.setAttribute('startOffset', '100%');
    } else {
      textNode.setAttribute('text-anchor', 'middle');
      textPath.setAttribute('startOffset', '50%');
    }

    if (!path.id) {
      path.id = 'gravure-preview-path-' + Math.random().toString(36).slice(2, 8);
    }
    if (textPath.getAttribute('href') !== '#' + path.id) {
      textPath.setAttribute('href', '#' + path.id);
      textPath.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '#' + path.id);
    }

    var rise = curve * zoneWidth;
    var svgHeight = fontSize * 2 + Math.abs(rise);
    var baseline = rise >= 0 ? svgHeight - fontSize : fontSize;
    var control = baseline - 2 * rise;

    svg.setAttribute('width', zoneWidth);
    svg.setAttribute('height', svgHeight);
    svg.setAttribute('viewBox', '0 0 ' + zoneWidth + ' ' + svgHeight);
    path.setAttribute('d', 'M0,' + baseline + ' Q' + (zoneWidth / 2) + ',' + control + ' ' + zoneWidth + ',' + baseline);

    textNode.setAttribute('font-size', fontSize);

    // Réduit la police si le texte dépasse l'arc (marge de 4 %).
    var pathLength = path.getTotalLength();
    var textLength = textNode.getComputedTextLength ? textNode.getComputedTextLength() : 0;
    if (pathLength && textLength > pathLength * 0.96) {
      textNode.setAttribute('font-size', Math.max(fontSize * (pathLength * 0.96) / textLength, 6));
    }
  }

  // Le graveur ne connaît qu'un seul cœur, plein : toutes les variantes saisies sont affichées ainsi.
  var HEART = '\u2665';
  var HEART_VARIANTS = /[\u2661\u2764\u2765\u2763\u{1F90D}\u{1F5A4}\u{1F499}\u{1F49A}\u{1F49B}\u{1F49C}\u{1F9E1}\u{1F495}\u{1F496}\u{1F497}\u{1F493}\u{1F49E}\u{1F49D}]\uFE0F?/gu;

  function update(text) {
    state.text = (text || '').toString().replace(HEART_VARIANTS, HEART).replace(/\uFE0F/g, '');
    var hasHeart = state.text.indexOf(HEART) !== -1;

    state.previews.forEach(function (preview) {
      preview.querySelectorAll('[data-engraving-preview-text]').forEach(function (el) {
        el.textContent = state.text;
      });
      preview.classList.toggle('has-text', state.text.length > 0);
      // Avec un cœur, le graveur passe en Arial : la prévisualisation suit.
      preview.classList.toggle('gravure-preview--heart', hasHeart);
      layout(preview);
    });
  }

  function init() {
    var modal = document.querySelector('.modalGravure[data-engraving-images]');
    if (!modal) {
      return;
    }

    var images = (modal.getAttribute('data-engraving-images') || '').split('|').filter(Boolean);
    var ratios = (modal.getAttribute('data-engraving-ratios') || '').split('|').filter(Boolean);

    state.zone = resolveZone(modal);

    // Image dédiée à la prévisualisation : elle remplace le packshot désigné par zone.image.
    var customImage = modal.getAttribute('data-engraving-custom-image');
    if (customImage) {
      images = [customImage];
      ratios = [modal.getAttribute('data-engraving-custom-ratio') || ''];
      state.zone.image = 0;
    }
    state.previews = Array.prototype.slice.call(modal.querySelectorAll('[data-engraving-preview]'));
    state.previews.forEach(function (preview) {
      applyZone(preview, state.zone, images, ratios);
    });

    if ('ResizeObserver' in window) {
      var observer = new ResizeObserver(function () {
        state.previews.forEach(layout);
      });
      state.previews.forEach(function (preview) {
        observer.observe(preview);
      });
    } else {
      window.addEventListener('resize', function () {
        state.previews.forEach(layout);
      });
    }

    var input = document.getElementById('limited');
    if (input) {
      update(input.value);
    }
  }

  window.theme = window.theme || {};
  window.theme.engravingPreview = {
    init: init,
    update: update,
    defaultZones: DEFAULT_ZONES
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
